import { useMemo, useCallback } from 'react'
import { Text, Flex, CardFooter, Button, ToggleMe, ButtonMenu, useMatchBreakpointsContext } from '@pancakeswap/uikit'
import Link from 'next/link'
import { useTranslation } from '@pancakeswap/localization'
import { useWeb3React } from '@web3-react/core'
import { useTokenBalancesWithLoadingIndicator } from 'state/wallet/hooks'
import { usePairs, PairState } from 'hooks/usePairs'
import { toV2LiquidityToken, useTrackedTokenPairs } from 'state/user/hooks'
import Dots from 'components/Loader/Dots'
import { AppHeader } from 'components/App'
import Page from '../../Page/Page'
import PaginationControls from 'components/PaginationControls'
import { useState } from 'react'
import { MobileView, FlexConnect, Tabs, Body } from './styled'
import ConnectWalletButton from 'components/ConnectWalletButton'
import dynamic from 'next/dynamic'
import { WalletDroplet } from './styled'
import RingLoader from 'react-spinners/RingLoader'
import FullPositionCard from './components/FullPositionCard'

export enum LiquidityType {
  V2,
  V3,
}

const V3Liquidity = dynamic(() => import('../V3'), { ssr: true })

export default function Liquidity() {
  const { isMobile, isDesktop } = useMatchBreakpointsContext()
  const { account } = useWeb3React()
  const { t } = useTranslation()
  const trackedTokenPairs = useTrackedTokenPairs()

  const tokenPairsWithLiquidityTokens = useMemo(
    () => trackedTokenPairs.map((tokens) => ({ liquidityToken: toV2LiquidityToken(tokens), tokens })),
    [trackedTokenPairs],
  )
  const liquidityTokens = useMemo(
    () => tokenPairsWithLiquidityTokens.map((tpwlt) => tpwlt.liquidityToken),
    [tokenPairsWithLiquidityTokens],
  )
  const [v2PairsBalances, fetchingV2PairBalances] = useTokenBalancesWithLoadingIndicator(
    account ?? undefined,
    liquidityTokens,
  )

  const liquidityTokensWithBalances = useMemo(
    () =>
      tokenPairsWithLiquidityTokens.filter(({ liquidityToken }) =>
        v2PairsBalances[liquidityToken.address]?.greaterThan('0'),
      ),
    [tokenPairsWithLiquidityTokens, v2PairsBalances],
  )

  const v2Pairs = usePairs(liquidityTokensWithBalances.map(({ tokens }) => tokens))
  const v2IsLoading =
    fetchingV2PairBalances ||
    v2Pairs?.length < liquidityTokensWithBalances.length ||
    (v2Pairs?.length && v2Pairs.every(([pairState]) => pairState === PairState.LOADING))

  const allV2PairsWithLiquidity = v2Pairs
    ?.filter(([pairState, pair]) => pairState === PairState.EXISTS && Boolean(pair))
    .map(([, pair]) => pair)

  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 6

  const onPageChange = useCallback((page) => {
    setCurrentPage(page)
  }, [])
  const indexOfLastItem = currentPage * PAGE_SIZE
  const indexOfFirstItem = indexOfLastItem - PAGE_SIZE
  const currentItems = useMemo(
    () => allV2PairsWithLiquidity.slice(indexOfFirstItem, indexOfLastItem),
    [currentPage, allV2PairsWithLiquidity],
  )
  const totalPages = Math.ceil(allV2PairsWithLiquidity.length / PAGE_SIZE)

  const renderBody = () => {
    if (!account) {
      return (
        <FlexConnect>
          <ConnectWalletButton width="100%" />
        </FlexConnect>
      )
    }

    if (v2IsLoading) {
      return (
        <Flex justifyContent="center" alignItems="center" marginTop="2rem">
          <RingLoader
            color="#37C0DF"
            // loading={loading}
            // cssOverride={override}
            size={55}
            aria-setsize={55}
            speedMultiplier={0.9}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </Flex>
      )
    }

    if (currentItems.length > 0) {
      return currentItems.map((v2Pair, index) => (
        <FullPositionCard
          key={v2Pair.liquidityToken.address}
          pair={v2Pair}
          mb={index < currentItems.length - 1 ? '16px' : '32px'}
          mx="4px"
        />
      ))
    }

    return (
      <Text color="textSubtle" textAlign="center">
        {t('No liquidity found.')}
      </Text>
    )
  }

  const [view, setView] = useState(() => {
    const savedView = localStorage.getItem('liquidityView')
    return savedView ? JSON.parse(savedView) : LiquidityType.V2
  })

  const handleClick = (newIndex: number) => {
    setView(newIndex)
    localStorage.setItem('liquidityView', JSON.stringify(newIndex))
  }

  const TabsComponent: React.FC = () => (
    <Tabs>
      <ButtonMenu scale="md" onItemClick={handleClick} activeIndex={view} fullWidth>
        <ToggleMe style={{ height: 30, margin: '4px' }}>{t('STANDARD')}</ToggleMe>
        <ToggleMe style={{ height: 30, margin: '4px' }}>{t('CONCENTRATED')}</ToggleMe>
      </ButtonMenu>
    </Tabs>
  )

  return (
    <Page backgroundImageSrc="/images/vikings/clean/malevike.webp" backgroundDesktopOpacity={0.3}>
      <Flex flexDirection="column" alignItems="center" width="100%" justifyContent="center">
        <Text fontSize={isMobile ? '48px' : '68px'}>LIQUIDITY</Text>
      </Flex>
      <TabsComponent />
      {/* <MobileView width="100%">
          <Flex marginBottom="1rem">
            <TabsComponent />
          </Flex>
    </MobileView>

    <DesktopView flexDirection="column" alignItems="center" width="100%" justifyContent="center">
      <Text fontSize={isMobile ? '48px' : '68px'}>LIQUIDITY</Text>

      <TabsComponent />
    </DesktopView> */}

      {view === LiquidityType.V2 && (
        <Flex
          flexDirection="column"
          marginTop={isDesktop ? '0px' : '8px'}
          justifyContent="flex-start"
          minHeight="75vh"
          width="100%"
          alignItems="center"
        >
          <Flex flexDirection="column" width="100%" justifyContent="center" alignItems="center">
            <Flex
              marginBottom={isDesktop ? '1rem' : '1rem'}
              flexDirection="row"
              width="100%"
              justifyContent="center"
              backgroundColor=""
              alignItems="center"
            >

                <Button variant="primary" as="a" href="/add" scale="pawg" marginX="4px" my="8px">
                  {t('Add Liquidity')}
                </Button>

            </Flex>
          </Flex>
          <Body>{renderBody()}</Body>
          {allV2PairsWithLiquidity?.length > 6 && (
            <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
          )}

          <Flex flex={1} width="100%" alignItems="flex-end" justifyContent="center" marginTop="0rem">

              <Button variant="secondarytrans" scale="pawg" as="a" href="/find">
                {t('Find Other LP positions')}
              </Button>

          </Flex>
        </Flex>
      )}
      {view === LiquidityType.V3 && <V3Liquidity />}
    </Page>
  )
}
